// Pełny tekst w kolumnach gazetowych: gdy szerokość ekranu mieści co najmniej
// dwie kolumny najdłuższego wiersza utworu, tekst płynie kolumnami na całą
// wysokość ekranu, a kolejne kolumny stoją obok siebie i przewijają się w bok.
// Na wąskim telefonie w pionie zostaje jedna kolumna przewijana w dół, bo dwie
// wymagałyby czcionki nieczytelnej z odległości ręki trzymającej gitarę.
//
// Przewijanie w dół przy kolumnach wyrównanych do wysokości treści byłoby
// gorsze: bieżąca kolumna i następna miałyby wspólny pasek przewijania, więc
// przejście z dołu pierwszej na górę drugiej wymagałoby cofnięcia ekranu.

export const ODSTEP_KOLUMN_PX = 28;
/** Zapas na pasek podświetlenia wersu (`.pt-grupa`: ramka i wcięcie). */
const ZAPAS_KOLUMNY_PX = 16;

const BAZOWA_CZCIONKA_PX = 14;
const NAJWIEKSZA_CZCIONKA_PX = 20;

/** Rozmiar czcionki (px, całkowity), przy którym `najdluzszyWiersz` znaków
 * mieści się w `szerokoscPola`, w granicach od bazowej do 20 px. Szerokość
 * znaku czcionki o stałej szerokości rośnie liniowo z rozmiarem. */
export function rozmiarCzcionki(szerokoscPola: number, najdluzszyWiersz: number, szerokoscZnaku: number, bazowa: number): number {
  if (najdluzszyWiersz <= 0 || szerokoscZnaku <= 0) return bazowa;
  const zmiesci = Math.floor((bazowa * szerokoscPola) / (najdluzszyWiersz * szerokoscZnaku));
  return Math.max(bazowa, Math.min(NAJWIEKSZA_CZCIONKA_PX, zmiesci));
}

/** Szerokość kolumny w px albo `null`, gdy mieści się tylko jedna. */
export function szerokoscKolumny(szerokoscPola: number, najdluzszyWiersz: number, szerokoscZnaku: number): number | null {
  if (najdluzszyWiersz <= 0 || szerokoscZnaku <= 0) return null;
  const kolumna = Math.ceil(najdluzszyWiersz * szerokoscZnaku) + ZAPAS_KOLUMNY_PX;
  return 2 * kolumna + ODSTEP_KOLUMN_PX <= szerokoscPola ? kolumna : null;
}

/**
 * Dokąd przewinąć w bok, żeby kolumna z bieżącym wersem stała po lewej, a
 * po prawej było widać, co przyjdzie potem. `null`, gdy przewijać nie trzeba:
 * wers stoi w pierwszej widocznej kolumnie. Kolumna z `x` liczona od
 * początku treści, a nie od lewej krawędzi ekranu.
 */
export function przewiniecieDoKolumny(xWersu: number, kolumna: number, przewiniete: number): number | null {
  const krok = kolumna + ODSTEP_KOLUMN_PX;
  const nr = Math.floor((xWersu + ODSTEP_KOLUMN_PX / 2) / krok);
  const cel = nr * krok;
  return Math.abs(cel - przewiniete) < krok / 2 ? null : cel;
}

/** Układa `.pt-container` w `tresc` w kolumny albo zdejmuje układ, gdy się
 * nie mieści. Zwraca szerokość kolumny, gdy układ jest włączony. */
export function ulozKolumny(app: HTMLElement, tresc: HTMLElement): number | null {
  const kontener = tresc.querySelector<HTMLElement>('.pt-container');
  if (!kontener) return null;
  const wiersze = [...kontener.querySelectorAll<HTMLElement>('.pt-ch, .pt-txt')];
  const najdluzszy = wiersze.reduce((m, w) => Math.max(m, (w.textContent ?? '').trimEnd().length), 0);
  kontener.style.removeProperty('font-size');
  const znak = szerokoscZnaku(kontener);
  const kolumna = szerokoscKolumny(app.clientWidth - 24, najdluzszy, znak);
  if (kolumna === null) {
    delete app.dataset.kolumny;
    kontener.style.removeProperty('--pt-kolumny');
    // Jedna kolumna: czcionka rośnie, aż najdłuższy wiersz wypełni szerokość.
    // Na iPhonie tekst zajmował połowę ekranu przy 14 px, a czyta się go z
    // odległości ręki na gryfie.
    const rozmiar = rozmiarCzcionki(app.clientWidth - 24 - ZAPAS_KOLUMNY_PX, najdluzszy, znak, BAZOWA_CZCIONKA_PX);
    if (rozmiar !== BAZOWA_CZCIONKA_PX) kontener.style.fontSize = `${rozmiar}px`;
    return null;
  }
  // Przeglądarka rozciąga kolumny na całą szerokość, więc ustawiamy ich
  // liczbę, a oddajemy szerokość faktyczną, bo od niej liczy się przewijanie.
  const pole = app.clientWidth - 24;
  const ile = Math.floor((pole + ODSTEP_KOLUMN_PX) / (kolumna + ODSTEP_KOLUMN_PX));
  const faktyczna = (pole - (ile - 1) * ODSTEP_KOLUMN_PX) / ile;
  app.dataset.kolumny = String(ile);
  kontener.style.setProperty('--pt-kolumny', String(ile));
  return faktyczna;
}

/** Szerokość jednego znaku czcionki tekstu (monospace), zmierzona. */
function szerokoscZnaku(kontener: HTMLElement): number {
  const proba = document.createElement('span');
  proba.className = 'pt-txt';
  proba.style.cssText = 'position:absolute;visibility:hidden;white-space:pre';
  proba.textContent = 'M'.repeat(40);
  kontener.appendChild(proba);
  const szer = proba.getBoundingClientRect().width / 40;
  proba.remove();
  return szer;
}
